import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import IconMale from "@/assets/icons/male.svg?react";
import GetDoneSuccessIcon from "@/assets/icons/getdon_success.svg?react";
import IconFemale from "@/assets/icons/female.svg?react";
import BaseButton from "@/components/base/button/BaseButton";
import AvatarUploader from "@/components/form/FormAvatarUploader";
import UsernameForm from "@/components/common/UsernameForm";
import { useUsernameCheck } from "@/shared/hooks/useUsernameCheck";
import { cn } from "@/lib/utils";
import { ProfileSchema, ProfilePayload } from "./profile.schema";
import { useFileUpload, useProfileUpdate } from "./edit-profile.api";
import RegionDistrictSelect from "@/pages/profile/edit/RegionDistrictSelect.tsx";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { formatPhoneMasked, useGetProfile, useSharePhone } from "../api";
import { RELEASE_P2P } from "@/config/launch.config";
import { toast } from "sonner";
import { formatNumber } from "@/shared/utils";
import { useFetchSettings } from "@/pages/home/api";
import PageWrapper from "@/components/PageWrapper.tsx";
import showCustomToast from "@/shared/utils/showCustomToast.ts";
import { getApiErrorMessage } from "@/shared/utils/getApiErrorMessage.ts";
import DatePickerDrawer from "./DatePickerDrawer";
import dayjs from "dayjs";
import { hapticFeedback } from "@telegram-apps/sdk-react";
import IconChevron from "@/assets/icons/chevron.svg?react";
import { Check, Loader2 } from "lucide-react";

const TOTAL_FIELDS = 7;
const PROGRESS_BAR_GRADIENT =
  "linear-gradient(90deg, #FAC228 0%, #E8A12D 100%)";

const RadioOnIcon = () => (
  <div className="size-6 bg-blue rounded-full shrink-0 flex-center">
    <div className="size-3 bg-white rounded-full" />
  </div>
);

const RadioOffIcon = () => (
  <div className="size-6 rounded-full bg-white/08 flex-center">
    <div className="size-5 rounded-full bg-gray-4" />
  </div>
);

const RadioActiveCase = ({ isActive }: { isActive: boolean }) =>
  isActive ? <RadioOnIcon /> : <RadioOffIcon />;

interface RadioCardProps {
  value: string;
  id: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
}

const RadioCard = ({ value, id, icon, label, isActive }: RadioCardProps) => (
  <div
    className={cn("flex grow w-full items-center rounded-10 overflow-hidden", {
      "border border-blue": isActive,
    })}
    onClick={() => {
      hapticFeedback.impactOccurred("soft");
    }}
  >
    <RadioGroupItem value={value} id={id} className="peer sr-only" />
    <Label htmlFor={id} className="w-full">
      <div className="flex items-center justify-between w-full p-2.5 bg-white/08 border-b border-white/08">
        <div
          className={cn(
            "flex-y-center gap-2.5 text-base text-white leading-130 font-normal",
            {
              "[&_svg]:!text-blue": isActive,
            }
          )}
        >
          {icon}
          {label}
        </div>
        <RadioActiveCase isActive={isActive} />
      </div>
    </Label>
  </div>
);

const ProfileInfoBanner = ({
  progress,
}: {
  progress: { percentage: number; count: number };
}) => {
  const { t } = useTranslation();
  const { data: settings } = useFetchSettings();

  return (
    <PageWrapper backPath="/profile">
      <div className="p-3 rounded-2xl border border-[#FFD230]/20 bg-[#FFD230]/[0.06] relative overflow-hidden">
        <h2 className="flex-y-center gap-2 text-base text-white font-semibold leading-normal relative z-20">
          <img width={28} height={28} src="images/user-info.svg" alt="" />
          {t("profile_infos")}
        </h2>
        <p className="max-w-[207px] my-2 text-small font-semibold leading-130 text-white relative z-20">
          {settings?.profile_completion_award &&
            t("enter_profile_infos_and_get_don", {
              don: formatNumber(settings?.profile_completion_award),
            })}
        </p>
        <div className="flex-y-center gap-1 relative z-20">
          <div className="w-[60%] h-4 bg-black/90 rounded-full overflow-hidden relative">
            <span
              className="absolute max-w-full left-0 transition-all top-0 h-full rounded-full"
              style={{
                width: `${progress.percentage}%`,
                background: PROGRESS_BAR_GRADIENT,
              }}
            />
          </div>
          <p className="text-[#FAC228] text-xs font-medium leading-130">
            {progress.count}/{TOTAL_FIELDS}
          </p>
        </div>
        <img
          className="h-full absolute right-0 bottom-0 z-10"
          src="images/patterns/profile-infos-don.webp"
          alt=""
        />
        <img
          className="absolute w-[443px] h-[253px] -right-16 -top-[34px] z-0 blur-[40px]"
          src="images/patterns/profile-infos.svg"
          alt=""
        />
      </div>
    </PageWrapper>
  );
};

const ProfileEdit = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo");
  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(
    null
  );
  const [isAvatarDeleted, setIsAvatarDeleted] = useState(false);
  const [hasAvatar, setHasAvatar] = useState(false);
  const {
    isUsernameAvailable,
    checkAvailable,
    checkDebouncing,
    checkLoading,
    isCheckingUsername,
    isDebouncing,
  } = useUsernameCheck();
  const { data: settings } = useFetchSettings();

  const {
    data: profileData,
    refetch,
    isLoading: isLoadingProfile,
  } = useGetProfile();
  const { mutateAsync: uploadAvatar, isPending: avatarUploading } =
    useFileUpload();
  const { mutateAsync: updateProfile, isPending: updatingProfile } =
    useProfileUpdate();
  const { sharePhone, isPending: isSettingPhone } = useSharePhone({
    onSuccess: () => {
      if (returnTo) {
        setTimeout(() => navigate(returnTo, { replace: true }), 350);
      }
    },
  });

  const form = useForm<ProfilePayload>({
    mode: "onChange",
    resolver: zodResolver(ProfileSchema),
    defaultValues: {
      full_name: profileData?.full_name ?? "",
      username: profileData?.username ?? undefined,
      birth_date: profileData?.birth_date ?? undefined,
      gender: profileData?.gender ?? undefined,
      region_id: profileData?.region?.id ?? undefined,
      district_id: profileData?.district?.id ?? undefined,
    },
  });

  const isAvatarChanged = useMemo(() => {
    return (
      selectedAvatarFile !== null || (isAvatarDeleted && !!profileData?.avatar)
    );
  }, [selectedAvatarFile, isAvatarDeleted, profileData?.avatar]);

  const hasChanges = form.formState.isDirty || isAvatarChanged;

  useEffect(() => {
    if (profileData?.avatar?.url) {
      setHasAvatar(true);
    }
  }, [profileData, form]);

  const deleteAvatar = useCallback(() => {
    setIsAvatarDeleted(true);
    setHasAvatar(false);
  }, []);

  const watchedFields = useWatch({ control: form.control });

  const progress = useMemo(() => {
    const filledFields = Object.values(watchedFields).filter(Boolean).length;
    const hasAvatarField =
      (hasAvatar && !isAvatarDeleted) || !!selectedAvatarFile;
    const totalFilledFields = filledFields + (hasAvatarField ? 1 : 0);

    return {
      percentage: Math.round((totalFilledFields / TOTAL_FIELDS) * 100),
      count: totalFilledFields,
    };
  }, [watchedFields, hasAvatar, isAvatarDeleted, selectedAvatarFile]);

  const handleAvatarFile = useCallback((file: File | null) => {
    setSelectedAvatarFile(file);
    if (file) {
      setHasAvatar(true);
      setIsAvatarDeleted(false);
    }
  }, []);

  const onSubmit = async (data: ProfilePayload) => {
    if (!hasChanges) {
      navigate("/profile");
      return;
    }

    // Username kiritilgan bo‘lsa va mavjud bo‘lmasa xato
    if (data.username && !isUsernameAvailable) {
      toast.error(t("username_not_available"));
      return;
    }

    try {
      let avatarId: string | null | undefined = profileData?.avatar?.id;
      if (selectedAvatarFile) {
        const response = await uploadAvatar({
          file: selectedAvatarFile,
          purpose: "PROFILE",
          type: "IMAGE",
        });
        avatarId = response.id;
      } else if (isAvatarDeleted) {
        avatarId = null;
      }

      if (data.region_id && !data.district_id) {
        toast.error(t("select_district_error"));
        return;
      }

      const payload: ProfilePayload = {
        full_name: data.full_name,
        username: data.username,
        birth_date: data.birth_date,
        gender: data.gender,
        avatar_id: avatarId,
        region_id: data.region_id,
        district_id: data.district_id,
      };

      await updateProfile(payload);

      await refetch();
      if (
        progress.percentage === 100 &&
        settings?.profile_completion_award &&
        !profileData?.profile_completion_awarded
      ) {
        showCustomToast(t("congratulations"), "success", {
          description: t("get_done_successfully", {
            don: formatNumber(settings?.profile_completion_award),
          }),
          icon: <GetDoneSuccessIcon className="size-7" />,
        });
      }
      navigate("/profile");
    } catch (error: any) {
      console.error("Profile update failed:", error);
      const errorMessage = getApiErrorMessage(
        error,
        t("profile_update_error") || "Profilni yangilashda xato yuz berdi"
      );
      toast.error(errorMessage);
    }
  };

  const isPending = updatingProfile || avatarUploading;
  const isFormValid = form.formState.isValid && watchedFields.full_name;

  return (
    <PageWrapper backPath="/profile">
      <div className="p-4 flex flex-col justify-between gap-4 min-h-screen">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <AvatarUploader
              onAvatarChange={handleAvatarFile}
              deleteAvatar={deleteAvatar}
              avatarUrl={profileData?.avatar?.url}
            />
            <div className="w-full h-px bg-white/08" />
            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("full_name")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("full_name_placeholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <RegionDistrictSelect isLoading={isLoadingProfile} />

            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("username")}</FormLabel>
                  <FormControl>
                    <UsernameForm
                      {...field}
                      onAvailabilityChange={checkAvailable}
                      initialUsername={profileData?.username ?? ""}
                      onCheckLoading={checkLoading}
                      onDebouncingChange={checkDebouncing}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Phone — non-editable once shared via Telegram requestContact().
                Released together with P2P; hidden until then. */}
            {RELEASE_P2P && (
            <FormItem>
              <FormLabel>{t("phone_number") || "Telefon raqam"}</FormLabel>
              {profileData?.phone ? (
                <div className="bg-gray-4 text-white p-2.5 rounded-10 border-b border-white/08 flex justify-between items-center gap-2">
                  <span className="overflow-hidden line-clamp-1 pr-2 w-full">
                    {formatPhoneMasked(profileData.phone)}
                  </span>
                  <div className="size-5 rounded-full bg-green/15 flex-center shrink-0">
                    <Check className="size-3 text-green" strokeWidth={3} />
                  </div>
                </div>
              ) : (
                <div
                  onClick={isSettingPhone ? undefined : sharePhone}
                  className={cn(
                    "bg-gray-4 text-white p-2.5 rounded-10 border border-orange-100/40 flex justify-between items-center transition-colors active:bg-[#101017]",
                    isSettingPhone
                      ? "cursor-not-allowed opacity-50"
                      : "cursor-pointer"
                  )}
                >
                  {isSettingPhone ? (
                    <Loader2 className="mx-auto size-5 animate-spin text-orange-100" />
                  ) : (
                    <>
                      <span className="overflow-hidden line-clamp-1 pr-2 w-full">
                        {t("share_phone_via_telegram") ||
                          "Telegram orqali ulashish"}
                      </span>
                      <IconChevron
                        className="h-5 w-5 text-orange-100 flex-shrink-0"
                        strokeWidth="1"
                      />
                    </>
                  )}
                </div>
              )}
            </FormItem>
            )}

            <FormField
              control={form.control}
              name="birth_date"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <DatePickerDrawer
                      onValueChange={(date) => {
                        form.setValue("birth_date", dayjs(date).format(), {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                      value={field.value}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("gender")}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex items-center w-full"
                    >
                      <RadioCard
                        value="MALE"
                        id="male"
                        icon={<IconMale className="w-6 h-6" />}
                        label={t("male")}
                        isActive={field.value === "MALE"}
                      />
                      <RadioCard
                        value="FEMALE"
                        id="female"
                        icon={<IconFemale className="w-6 h-6" />}
                        label={t("female")}
                        isActive={field.value === "FEMALE"}
                      />
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid gap-4">
              {!profileData?.profile_completion_awarded && (
                <ProfileInfoBanner progress={progress} />
              )}
              <BaseButton
                type="submit"
                text={t("save")}
                vibrate={true}
                vibrationType="soft"
                disabled={
                  isPending ||
                  isCheckingUsername ||
                  isDebouncing ||
                  !isFormValid
                }
                prefix={
                  isPending && (
                    <img
                      width="20"
                      height="20"
                      src="loader.svg"
                      alt="loading"
                      className="animate-spin-slow"
                    />
                  )
                }
              />
            </div>
          </form>
        </Form>
      </div>
    </PageWrapper>
  );
};

export default ProfileEdit;
